'use client';

import { useEffect } from 'react';

import {
  emitTrackViewHdpEvent,
  emitTrackViewHiddenListingsPageEvent,
  emitTrackViewHomepageEvent,
  emitTrackViewLoginPageEvent,
  emitTrackViewMyHubEvent,
  emitTrackViewMyListPageEvent,
  emitTrackViewSearchAlertsPageEvent,
  emitTrackViewSettingsPageEvent,
  emitTrackViewUserAccountPageEvent,
  emitTrackViewViewedListingsPageEvent,
} from './hotpads/events';
import type { HotpadsClickstreamListing } from './hotpads/types';
import { useSrpPageViewCoordinator } from './SrpPageViewCoordinator';

type HotpadsPageView =
  | 'hidden-listings'
  | 'hdp'
  | 'homepage'
  | 'login'
  | 'my-hub'
  | 'my-list'
  | 'profile'
  | 'search-alerts'
  | 'settings'
  | 'srp'
  | 'user-account'
  | 'viewed-listings';

type HotpadsPageViewTrackerProps = {
  event: HotpadsPageView;
  isAgentic?: boolean;
  listing?: HotpadsClickstreamListing | null;
  settingsTrigger?: 'edit_profile_page' | 'renter_profile' | 'edit_settings_page';
};

function SrpPageViewTracker({ isAgentic }: { isAgentic: boolean }) {
  const { trackMount } = useSrpPageViewCoordinator();

  useEffect(() => {
    trackMount(isAgentic);
  }, [isAgentic, trackMount]);

  return null;
}

export function HotpadsPageViewTracker({
  event,
  isAgentic = false,
  listing,
  settingsTrigger,
}: HotpadsPageViewTrackerProps) {
  useEffect(() => {
    if (event === 'srp') return;

    switch (event) {
      case 'hidden-listings':
        emitTrackViewHiddenListingsPageEvent();
        return;
      case 'hdp':
        emitTrackViewHdpEvent({ isAgentic, listing });
        return;
      case 'homepage':
        emitTrackViewHomepageEvent();
        return;
      case 'login':
        emitTrackViewLoginPageEvent();
        return;
      case 'my-hub':
        emitTrackViewMyHubEvent();
        return;
      case 'my-list':
        emitTrackViewMyListPageEvent();
        return;
      case 'profile':
        emitTrackViewUserAccountPageEvent();
        emitTrackViewSettingsPageEvent({
          triggerLocation: 'edit_profile_page',
          triggerSource: 'edit_profile_page',
        });
        return;
      case 'search-alerts':
        emitTrackViewSearchAlertsPageEvent();
        return;
      case 'settings':
        emitTrackViewSettingsPageEvent({
          triggerLocation: settingsTrigger ?? 'edit_settings_page',
          triggerSource: settingsTrigger ?? 'edit_settings_page',
        });
        return;
      case 'user-account':
        emitTrackViewUserAccountPageEvent();
        return;
      case 'viewed-listings':
        emitTrackViewViewedListingsPageEvent();
        return;
    }
  }, [event, isAgentic, listing, settingsTrigger]);

  return event === 'srp' ? <SrpPageViewTracker isAgentic={isAgentic} /> : null;
}
